import Image from "next/image";

import StarIcon from "@repo/ui-system/icons/rating/star/filled.svg";
import { Dialog } from "radix-ui";

import { type OutboundBiParams } from "@/lib/affiliate/build-redirect-url";
import type { BestStoreDeal } from "@/lib/api/best/keywords/schema";

import { OutboundLink } from "../../../outbound-link/outbound-link";
import { isStoreDealActive, StoreDealIndicator } from "../store-deal";

export type PopupSecondaryStore = {
  name: string;
  logo: string | null;
  url: string;
  bi: OutboundBiParams;
};

export type PopupPrimaryStore = {
  name: string;
  logo: string | null;
  url: string;
  bi: OutboundBiParams;
  deal: BestStoreDeal | null;
};

export type StorePopupContent = {
  badge: string;
  discountLabel: string | null;
  imageUrl: string;
  title: string;
  reviewCount: string | null;
  ctaLabel: string;
  primaryStore: PopupPrimaryStore;
  secondaryStores: PopupSecondaryStore[];
};

type Props = {
  content: StorePopupContent;
};

/**
 * Store-specific promotion popup content (COM-681).
 *
 * Shows the primary store (stores[0]) prominently — logo + CTA + its own deal —
 * and lists the remaining stores under an "Also at:" row as logo buttons.
 * Duplicated from PromotionPopupContent; the original is left untouched.
 */
export function StorePromotionPopupContent({ content }: Props) {
  const { primaryStore, secondaryStores } = content;

  return (
    <div className="relative z-10 flex flex-col gap-5 p-5 bg-[linear-gradient(8deg,#FFF_47.41%,#F0F3FF_95.27%)] rounded-2xl">
      {/* Badges + Image */}
      <div className="flex flex-col items-center w-full">
        <div className="flex items-start justify-between w-full mb-[-20px] z-10">
          <div className="flex flex-col gap-2 items-start">
            <Dialog.Title asChild>
              <span
                className="bg-badge-first-bg text-badge-first-text px-4 py-2 sub1 font-semibold"
                style={{ borderRadius: "8px" }}
              >
                {content.badge}
              </span>
            </Dialog.Title>
            {content.discountLabel && (
              <span
                className="bg-badge-sale-bg text-badge-sale-text px-2 py-1 mini2"
                style={{ borderRadius: "8px" }}
              >
                {content.discountLabel}
              </span>
            )}
          </div>
        </div>
        <div className="size-[132px] mix-blend-darken">
          <Image
            src={content.imageUrl}
            alt={content.title}
            width={132}
            height={132}
            sizes="132px"
            className="size-[132px] object-contain"
          />
        </div>
      </div>

      {/* Reviews + Title */}
      <div className="flex flex-col gap-4 items-start w-full">
        {content.reviewCount && (
          <div className="flex gap-2 items-center px-2 py-0.5 bg-surface-primary-subtle rounded-[6px]">
            <StarIcon
              className="size-4 text-elements-random-yellow"
              aria-hidden="true"
            />
            <span className="mini1 text-text-grayscale-caption">
              Based on {content.reviewCount} Reviews
            </span>
          </div>
        )}
        <p className="mini2 line-clamp-2 text-text-grayscale-subtitle">
          {content.title}
        </p>
      </div>

      {/* Primary store CTA + its deal */}
      <div className="flex flex-col gap-4 w-full">
        <OutboundLink
          url={primaryStore.url}
          storeName={primaryStore.name}
          bi={primaryStore.bi}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col rounded-xl border border-border-primary-subtle overflow-hidden"
        >
          <div className="flex flex-col gap-2 items-center bg-surface-grayscale-default px-3 py-2">
            <div className="flex items-center justify-center h-5">
              {primaryStore.logo ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={primaryStore.logo}
                  alt={primaryStore.name}
                  className="h-5 w-auto object-contain"
                />
              ) : (
                <span className="mini1 text-text-grayscale-caption">
                  {primaryStore.name}
                </span>
              )}
            </div>
            <span className="flex items-center justify-center w-full bg-cta lg:hover:bg-cta-hover lg:transition-colors text-grayscale-white font-medium body1 rounded-lg py-2 px-4">
              {content.ctaLabel}
            </span>
          </div>
          {isStoreDealActive(primaryStore.deal) && (
            <div className="flex items-center justify-center bg-surface-grayscale-default px-3 pt-1 pb-2">
              <StoreDealIndicator
                type={primaryStore.deal.type}
                endTime={primaryStore.deal.endTime}
                variant="popup"
              />
            </div>
          )}
        </OutboundLink>

        {/* Also at: secondary stores */}
        {secondaryStores.length > 0 && (
          <>
            <div className="bg-border-grayscale-default h-px w-full" />
            <div className="flex flex-col gap-2 items-center w-full">
              <span className="body2 text-text-grayscale-body">Also at:</span>
              <div className="flex gap-2 items-center w-full">
                {secondaryStores.map((store, index) => (
                  <OutboundLink
                    key={index}
                    url={store.url}
                    storeName={store.name}
                    bi={store.bi}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-1 items-center justify-center bg-surface-grayscale-lighter rounded-lg px-4 py-2"
                  >
                    {store.logo ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={store.logo}
                        alt={store.name}
                        className="h-4 w-auto object-contain"
                      />
                    ) : (
                      <span className="mini1 text-text-grayscale-caption">
                        {store.name}
                      </span>
                    )}
                  </OutboundLink>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
